/**
 * SoundService.ts
 * A premium UI sound engine using the Web Audio API. 
 * Provides subtle, synthetic "earcons" for a multi-sensory experience.
 */

import { useSettingsStore } from '@/store/settingsStore';

class SoundService {
    private ctx: AudioContext | null = null;

    private init() {
        const { muteAudio } = useSettingsStore.getState();
        if (muteAudio) {
            this.ctx = null;
            return;
        }

        if (!this.ctx) {
            this.ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
        }
    }

    /**
     * Subtle high-end "click" for secondary interactions
     */
    public playTick() {
        this.init();
        if (!this.ctx) return;

        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();

        osc.type = 'sine';
        osc.frequency.setValueAtTime(800, this.ctx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(400, this.ctx.currentTime + 0.05);

        gain.gain.setValueAtTime(0.05, this.ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, this.ctx.currentTime + 0.05);

        osc.connect(gain);
        gain.connect(this.ctx.destination);

        osc.start();
        osc.stop(this.ctx.currentTime + 0.05);
    }

    /**
     * Soft, success-triumph chime for positive completions
     */
    public playSuccess() {
        this.init();
        const c = this.ctx;
        if (!c) return;

        const notes = [440, 554.37, 659.25]; // A4, C#5, E5 (Major triad)
        const now = c.currentTime;

        notes.forEach((freq, i) => {
            const osc = c.createOscillator();
            const gain = c.createGain();

            osc.type = 'sine';
            osc.frequency.setValueAtTime(freq, now + i * 0.05);

            gain.gain.setValueAtTime(0, now + i * 0.05);
            gain.gain.linearRampToValueAtTime(0.03, now + i * 0.05 + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.01, now + i * 0.05 + 0.3);

            osc.connect(gain);
            gain.connect(c.destination);

            osc.start(now + i * 0.05);
            osc.stop(now + i * 0.05 + 0.3);
        });
    }

    /**
     * Gentle "pop" for notifications or menu opens
     */
    public playPop() {
        this.init();
        const c = this.ctx;
        if (!c) return;

        const osc = c.createOscillator();
        const gain = c.createGain();

        osc.type = 'sine';
        osc.frequency.setValueAtTime(200, c.currentTime);
        osc.frequency.exponentialRampToValueAtTime(600, c.currentTime + 0.1);

        gain.gain.setValueAtTime(0.04, c.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, c.currentTime + 0.1);

        osc.connect(gain);
        gain.connect(c.destination);

        osc.start();
        osc.stop(c.currentTime + 0.1);
    }

    /**
     * Low-frequency "thud" for errors or cancellations
     */
    public playError() {
        this.init();
        const c = this.ctx;
        if (!c) return;

        const osc = c.createOscillator();
        const gain = c.createGain();

        osc.type = 'triangle';
        osc.frequency.setValueAtTime(150, c.currentTime);
        osc.frequency.linearRampToValueAtTime(80, c.currentTime + 0.2);

        gain.gain.setValueAtTime(0.05, c.currentTime);
        gain.gain.linearRampToValueAtTime(0, c.currentTime + 0.2);

        osc.connect(gain);
        gain.connect(c.destination);

        osc.start();
        osc.stop(c.currentTime + 0.2);
    }
}

export const soundService = new SoundService();
